import { useEffect, useState } from 'react';
import { NotebookPen } from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { readSessionNotes, saveSessionNotes, type SessionNotes } from '../../lib/sessionNotes';
import { Button, Card, TextAreaField } from '../charte';

/**
 * « Mes notes de la séance » (J31) : avant et pendant la séance, dès la première leçon. Enregistrées au fil de la
 * frappe sur cet appareil. Depuis le 30/10/2026 sur la page « Livret et résumé de la séance » : `onReuseDuring`
 * reprend les notes de pendant la séance dans la synthèse.
 */
export function SessionNotesCard({ candidateId, onReuseDuring }: { candidateId: string; onReuseDuring?: (during: string) => void }) {
  const userId = useAuthStore((s) => s.user?.id);
  const [notes, setNotes] = useState<SessionNotes>(() => readSessionNotes(userId, candidateId));
  useEffect(() => setNotes(readSessionNotes(userId, candidateId)), [userId, candidateId]);
  const change = (field: keyof SessionNotes, value: string) => {
    const next = { ...notes, [field]: value };
    setNotes(next);
    saveSessionNotes(userId, candidateId, next);
  };
  return (
    <Card className="space-y-4 p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
          <NotebookPen size={16} aria-hidden />
        </span>
        <div>
          <h2 className="text-title-sm text-heading">Mes notes de la séance</h2>
          <p className="mt-0.5 text-sm text-secondary">
            Gardées sur cet appareil jusqu’à l’enregistrement du résumé. L’élève ne les voit pas.
          </p>
        </div>
      </div>
      <TextAreaField
        label="Avant la séance"
        rows={3}
        maxLength={2000}
        value={notes.before}
        onChange={(e) => change('before', e.target.value)}
        placeholder="Ce que vous prévoyez de travailler, points à vérifier…"
        className="text-[15px]"
      />
      <div>
        <TextAreaField
          label="Pendant la séance"
          rows={4}
          maxLength={2000}
          value={notes.during}
          onChange={(e) => change('during', e.target.value)}
          placeholder="Ce que vous observez au fil de la conduite…"
          className="text-[15px]"
        />
        {onReuseDuring && notes.during.trim() && (
          <Button variant="link" size="sm" className="mt-1" onClick={() => onReuseDuring(notes.during.trim())}>
            Reprendre dans la synthèse
          </Button>
        )}
      </div>
    </Card>
  );
}
